import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";

import { useSelfUpdate } from "../../api/queries";
import type { NotificationChannel } from "../../api/types";
import { useAuth } from "../../auth/auth-context";
import { SettingsPage } from "../../ui/SettingsSection";
import { Toggle } from "../../ui/Toggle";
import { useToast } from "../../ui/toast-context";
import { fullSettingsMap, notificationGroups, resolveSetting } from "./notifications";

/**
 * The event table from `docs/spec/notifications.md` §4, with the per-channel
 * resolution of §5. Every change sends the **whole** map through `users.update`,
 * which is what the spec's per-channel form requires — the two subscribe
 * endpoints are the boolean fast path used by unsubscribe links, not by this
 * screen.
 */
export function NotificationSettingsScreen(): ReactNode {
  const { session } = useAuth();
  const settings = session?.info.user.notificationSettings;
  const update = useSelfUpdate();
  const toast = useToast();
  const [params] = useSearchParams();

  const change = (key: string, channel: NotificationChannel, value: boolean): void => {
    update.mutate(
      { notificationSettings: fullSettingsMap(settings, { key, channel, value }) },
      {
        onError: (error) => {
          toast.show(error.message, "error");
        },
      },
    );
  };

  return (
    <SettingsPage
      title="Notifications"
      description="What reaches you, and whether it arrives in the app, by email, or both."
    >
      {params.has("success") && (
        <p
          role="status"
          className="mb-6 rounded-md border border-line bg-surface px-3 py-2 text-sm"
        >
          You have been unsubscribed. Change your mind below at any time.
        </p>
      )}
      {params.get("notice") === "invalid-auth" && (
        <p
          role="alert"
          className="mb-6 rounded-md border border-danger px-3 py-2 text-sm text-danger"
        >
          That unsubscribe link is no longer valid. You can change any setting here instead.
        </p>
      )}

      {notificationGroups.map((group) => (
        <section key={group.name} className="mb-8">
          <h3 className="mb-2 text-sm font-semibold tracking-wide text-muted uppercase">
            {group.name}
          </h3>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs text-muted">
                <th scope="col" className="py-2 font-medium">
                  Event
                </th>
                <th scope="col" className="w-20 py-2 text-center font-medium">
                  In app
                </th>
                <th scope="col" className="w-20 py-2 text-center font-medium">
                  Email
                </th>
              </tr>
            </thead>
            <tbody>
              {group.events.map((event) => (
                <tr key={event.key} className="border-b border-line last:border-b-0">
                  <th scope="row" className="py-3 pr-4 text-left font-normal">
                    <span className="font-medium">{event.label}</span>
                    {event.description !== "" && (
                      <span className="block text-xs text-muted">{event.description}</span>
                    )}
                  </th>
                  {(["app", "email"] as NotificationChannel[]).map((channel) => (
                    <td key={channel} className="py-3 text-center">
                      {event.channels.includes(channel) ? (
                        <Toggle
                          compact
                          label={`${event.label} — ${channel === "app" ? "in app" : "email"}`}
                          checked={resolveSetting(settings?.[event.key], channel, event.defaultOn)}
                          onChange={(value) => {
                            change(event.key, channel, value);
                          }}
                        />
                      ) : (
                        <span aria-hidden="true" className="text-muted">
                          —
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </SettingsPage>
  );
}
